import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Plus, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { http, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../context/CategoriesContext";
import { AdminNav } from "../components/AdminNav";
import { ColorSwatchPicker } from "../components/admin/ColorSwatchPicker";
import { HexColorPicker } from "../components/admin/HexColorPicker";
import { SortableRow } from "../components/admin/SortableRow";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function AdminCategories() {
  const { user } = useAuth();
  const { categories, refresh } = useCategories();
  const [items, setItems] = useState([]);
  const [pdfCounts, setPdfCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "slate" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setItems(categories);
    if (categories.length) setLoading(false);
  }, [categories]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/pdfs");
        if (cancelled) return;
        const map = {};
        for (const p of data) map[p.folder] = (map[p.folder] || 0) + 1;
        setPdfCounts(map);
      } catch (e) {
        // Per-row PDF count is a nice-to-have; surface the issue without breaking the page.
        // eslint-disable-next-line no-console
        console.warn("Failed to load PDF counts for categories:", e?.message || e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const itemIds = useMemo(() => items.map((i) => i.id), [items]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered); // optimistic
    try {
      await http.post("/categories/reorder", { order: reordered.map((c) => c.id) });
      await refresh();
      toast.dismiss();
      toast.success("Order saved");
    } catch (e) {
      setItems(items); // revert
      toast.dismiss();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save order");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", color: "slate" });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, color: cat.color });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await http.patch(`/categories/${editing.id}`, form);
        toast.dismiss();
        toast.success("Category updated");
      } else {
        await http.post("/categories", form);
        toast.dismiss();
        toast.success("Category created");
      }
      setDialogOpen(false);
      await refresh();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await http.delete(`/categories/${deleteTarget.id}`);
      toast.dismiss();
      toast.success("Category deleted");
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      toast.dismiss();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
      setDeleteTarget(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <AdminNav />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Categories
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Reorder by dragging. Renaming a category updates every PDF that uses it.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg h-11"
          data-testid="category-create-btn"
        >
          <Plus className="h-4 w-4" />
          New Category
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-white border border-slate-200 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <h3 className="font-display text-xl font-bold text-slate-900">No categories yet</h3>
          <p className="text-sm text-slate-500 mt-1">Create your first category to organize PDFs.</p>
          <Button
            onClick={openCreate}
            className="mt-5 bg-brand-primary hover:bg-brand-primary-hover text-white"
            data-testid="category-empty-create-btn"
          >
            <Plus className="h-4 w-4" /> New Category
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="categories-list">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <ul>
                {items.map((cat) => (
                  <SortableRow
                    key={cat.id}
                    category={cat}
                    pdfCounts={pdfCounts}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold">
              {editing ? "Edit category" : "New category"}
            </DialogTitle>
            <DialogDescription>
              Use a short, descriptive name. Pick a color so users can recognize it at a glance.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="category-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Track & Field"
                required
                autoFocus
                data-testid="category-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Color</Label>
              <ColorSwatchPicker value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
              <div className="flex items-center gap-3 pt-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                  Or custom
                </span>
                <HexColorPicker value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
              </div>
            </div>

            {formError && (
              <div className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2" data-testid="category-form-error">
                {formError}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-brand-accent hover:bg-brand-accent-hover text-brand-primary font-bold"
                data-testid="category-form-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{deleteTarget?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This category will be removed. PDFs that still use it will block deletion — reassign or remove
              those PDFs first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="category-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
